// Shared RTCPeerConnection helpers used by both the widget and the agent
// app. Browser-only — the Node server never imports this file. Surfaced via
// the `@prakash-software/shared/webrtc` subpath export so it cannot accidentally be
// pulled into a server build.
//
// Section 5.2: STUN-only iceServers. TURN is deferred (Section 5.3) but the
// `onIceFailed` hook fires on every `iceConnectionState === 'failed'` so the
// caller can ship the metric that the Section 14.10 trigger relies on.

import type { RTCIceCandidateLike, RTCSessionDescriptionLike } from '../events/schema';

const STUN_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export function defaultIceServers(): RTCIceServer[] {
  return STUN_SERVERS.slice();
}

export interface PeerOptions {
  /** Fires for every local ICE candidate produced by gathering. */
  onLocalIce: (candidate: RTCIceCandidateLike) => void;
  /** Fires once a remote track lands — caller wires it to a <video>. */
  onRemoteTrack: (stream: MediaStream) => void;
  /** Fires on `iceConnectionState === 'failed'`. Used for the 5.3 counter. */
  onIceFailed?: () => void;
  /** Optional connection-state observer for status text in UI. */
  onConnectionState?: (state: RTCPeerConnectionState) => void;
  /**
   * Fires when the link is lost for good — the peer's connection went `failed`,
   * or `disconnected` and didn't recover within a short grace. This is how we
   * detect the OTHER party reloading / crashing / dropping (their PC vanishes,
   * so no `webrtc:hangup` is ever sent): the surviving side tears the call down
   * instead of staring at a frozen remote feed.
   */
  onPeerLost?: () => void;
}

/** A `disconnected` that recovers within this window is treated as a transient
 *  blip; longer than this and we declare the peer lost. */
const PEER_LOST_GRACE_MS = 4000;

export function createPeer(opts: PeerOptions): RTCPeerConnection {
  const pc = new RTCPeerConnection({ iceServers: defaultIceServers() });

  pc.onicecandidate = (e) => {
    if (!e.candidate) return;
    opts.onLocalIce({
      candidate: e.candidate.candidate,
      sdpMid: e.candidate.sdpMid ?? null,
      sdpMLineIndex: e.candidate.sdpMLineIndex ?? null,
    });
  };

  pc.ontrack = (e) => {
    const stream = e.streams[0];
    if (stream) opts.onRemoteTrack(stream);
  };

  // Peer-loss detection. We read both connectionState and iceConnectionState
  // (browsers differ in which transitions first) and debounce `disconnected`
  // so a brief network blip that recovers doesn't end the call.
  let lostTimer: ReturnType<typeof setTimeout> | null = null;
  const clearLost = () => {
    if (lostTimer) {
      clearTimeout(lostTimer);
      lostTimer = null;
    }
  };
  function evaluateLink() {
    if (!opts.onPeerLost) return;
    const cs = pc.connectionState;
    if (cs === 'closed') {
      clearLost();
      return; // we closed it ourselves — not a peer loss
    }
    const ice = pc.iceConnectionState;
    if (cs === 'connected' || ice === 'connected' || ice === 'completed') {
      clearLost();
      return;
    }
    if (cs === 'failed' || ice === 'failed') {
      clearLost();
      opts.onPeerLost();
      return;
    }
    if ((cs === 'disconnected' || ice === 'disconnected') && !lostTimer) {
      lostTimer = setTimeout(() => {
        lostTimer = null;
        if (pc.connectionState !== 'closed') opts.onPeerLost?.();
      }, PEER_LOST_GRACE_MS);
    }
  }

  pc.oniceconnectionstatechange = () => {
    if (pc.iceConnectionState === 'failed') opts.onIceFailed?.();
    evaluateLink();
  };

  pc.onconnectionstatechange = () => {
    opts.onConnectionState?.(pc.connectionState);
    evaluateLink();
  };

  return pc;
}

export function addLocalTracks(pc: RTCPeerConnection, stream: MediaStream): void {
  stream.getTracks().forEach((track) => pc.addTrack(track, stream));
}

export async function makeOffer(pc: RTCPeerConnection): Promise<RTCSessionDescriptionLike> {
  const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
  await pc.setLocalDescription(offer);
  return { type: 'offer', sdp: offer.sdp ?? '' };
}

export async function acceptOffer(
  pc: RTCPeerConnection,
  remote: RTCSessionDescriptionLike,
): Promise<RTCSessionDescriptionLike> {
  await pc.setRemoteDescription(remote as RTCSessionDescriptionInit);
  const answer = await pc.createAnswer();
  await pc.setLocalDescription(answer);
  return { type: 'answer', sdp: answer.sdp ?? '' };
}

export async function applyAnswer(
  pc: RTCPeerConnection,
  remote: RTCSessionDescriptionLike,
): Promise<void> {
  await pc.setRemoteDescription(remote as RTCSessionDescriptionInit);
}

export async function addRemoteIce(
  pc: RTCPeerConnection,
  candidate: RTCIceCandidateLike,
): Promise<void> {
  if (!candidate.candidate) return;
  await pc.addIceCandidate(candidate as RTCIceCandidateInit);
}

/** Replace the first sender's video track — used by 5.10 screen-share. */
export async function replaceVideoTrack(
  pc: RTCPeerConnection,
  track: MediaStreamTrack | null,
): Promise<void> {
  const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
  if (sender) await sender.replaceTrack(track);
}

export function closePeer(pc: RTCPeerConnection): void {
  pc.getSenders().forEach((s) => {
    try {
      s.track?.stop();
    } catch {
      /* noop */
    }
  });
  pc.onicecandidate = null;
  pc.ontrack = null;
  pc.oniceconnectionstatechange = null;
  pc.onconnectionstatechange = null;
  pc.close();
}
